import { useState, useEffect } from 'react';
import { X, Bell, CheckCircle, Loader2 } from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import { useAuth } from '../contexts/AuthContext';
import { trackEvent } from '../lib/firebase';
import {
  subscribeToWebPush,
  isPushNotificationSupported,
  isPushSubscribed,
} from '../lib/pushNotifications';

const PERMISSION_PROMPT_KEY = 'notification_permission_prompted';
const PERMISSION_DISMISSED_KEY = 'notification_permission_dismissed';
const PERMISSION_SNOOZE_KEY = 'notification_snooze_until';
const PERMISSION_DISMISS_COUNT_KEY = 'notification_dismiss_count';
const PAGES_VISITED_KEY = 'notification_pages_visited';
const MAX_DISMISSALS = 2;
const SNOOZE_DAYS = 7;

// Mobile/iOS detection
const isIOS = () => {
  try {
    return /iPad|iPhone|iPod/.test(navigator.userAgent) && !(window as any).MSStream;
  } catch {
    return false;
  }
};

const isMobile = () => {
  try {
    return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
  } catch {
    return false;
  }
};

const isSafari = () => {
  try {
    return /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
  } catch {
    return false;
  }
};

export function NotificationPermissionPrompt() {
  const [visible, setVisible] = useState(false);
  const [permission, setPermission] = useState<NotificationPermission>('default');
  const [isLoading, setIsLoading] = useState(false);
  const [magicBellStatus, setMagicBellStatus] = useState<'idle' | 'connecting' | 'success' | 'error'>('idle');
  const [showMobileButton, setShowMobileButton] = useState(false);
  const { user } = useAuth();

  useEffect(() => {
    // Check if browser supports notifications
    if (!('Notification' in window)) {
      console.log('📢 Browser does not support notifications');
      return;
    }

    // Don't show if user is not authenticated
    if (!user) {
      return;
    }

    // Get current permission status
    const currentPermission = Notification.permission;
    setPermission(currentPermission);

    // If permission already granted, check if we need to create subscription
    if (currentPermission === 'granted') {
      const isAlreadySubscribed = isPushSubscribed();
      if (!isAlreadySubscribed) {
        console.log('📱 Permission granted but no subscription - creating now...');
        // Create subscription automatically for users who already granted permission
        subscribeToWebPush(user.uid, user.email || '')
          .then((success) => {
            if (success) {
              console.log('✅ Auto-subscribed user with existing permission');
            }
          })
          .catch((err) => {
            console.error('❌ Auto-subscription failed:', err);
          });
      }
      return;
    }

    // Don't show if permission already denied
    if (currentPermission === 'denied') {
      return;
    }

    // Check if permanently dismissed
    const wasDismissed = localStorage.getItem(PERMISSION_DISMISSED_KEY);
    if (wasDismissed === 'true') {
      return;
    }

    // Check dismiss count
    const dismissCount = parseInt(localStorage.getItem(PERMISSION_DISMISS_COUNT_KEY) || '0', 10);
    if (dismissCount >= MAX_DISMISSALS) {
      localStorage.setItem(PERMISSION_DISMISSED_KEY, 'true');
      return;
    }

    // Check if snoozed
    const snoozeUntil = localStorage.getItem(PERMISSION_SNOOZE_KEY);
    if (snoozeUntil) {
      const snoozeDate = new Date(snoozeUntil);
      if (snoozeDate > new Date()) {
        console.log('📢 Notification prompt snoozed until:', snoozeDate);
        return;
      }
    }

    // Track page visits for engagement
    const currentPath = window.location.pathname;
    const visitedPages = JSON.parse(localStorage.getItem(PAGES_VISITED_KEY) || '[]') as string[];
    
    if (!visitedPages.includes(currentPath)) {
      visitedPages.push(currentPath);
      localStorage.setItem(PAGES_VISITED_KEY, JSON.stringify(visitedPages));
    }

    // For mobile devices (especially iOS), show immediately on lobby
    // iOS requires user gesture to request permissions, so we show a button
    if (isMobile()) {
      // Check if already prompted in this session
      const hasBeenPrompted = sessionStorage.getItem(PERMISSION_PROMPT_KEY);
      if (!hasBeenPrompted) {
        console.log('📢 Mobile device detected, showing notification button');
        setShowMobileButton(true);
        sessionStorage.setItem(PERMISSION_PROMPT_KEY, 'true');
        
        // Track analytics
        trackEvent('notification_prompt_shown', {
          pages_visited: visitedPages.length,
          dismiss_count: dismissCount,
          is_mobile: true,
          is_ios: isIOS(),
        });
      }
      return;
    }

    // Desktop: Only show if user has visited at least 2 pages (indicates engagement)
    if (visitedPages.length < 2) {
      console.log('📢 User needs to visit more pages before showing prompt');
      return;
    }

    // Check if already prompted in this session
    const hasBeenPrompted = sessionStorage.getItem(PERMISSION_PROMPT_KEY);
    if (hasBeenPrompted) {
      return;
    }

    // Show after a short delay to avoid overwhelming on first load (desktop only)
    const timer = setTimeout(() => {
      setVisible(true);
      sessionStorage.setItem(PERMISSION_PROMPT_KEY, 'true');
      
      // Track analytics
      trackEvent('notification_prompt_shown', {
        pages_visited: visitedPages.length,
        dismiss_count: dismissCount,
        is_mobile: false,
      });
    }, 3000);

    return () => clearTimeout(timer);
  }, [user]);

  const handleRequestPermission = async () => {
    if (!user) {
      console.error('❌ No user found for notification permission');
      return;
    }

    setIsLoading(true);
    setMagicBellStatus('idle');

    try {
      // Step 1: Request browser notification permission
      const result = await Notification.requestPermission();
      setPermission(result);
      
      // Track analytics
      trackEvent('notification_permission_response', {
        result,
        user_id: user.uid,
      });
      
      if (result === 'granted') {
        console.log('✅ Notification permission granted');
        
        // Step 2: Subscribe to web push notifications
        setMagicBellStatus('connecting');
        const pushSuccess = await subscribeToWebPush(user.uid, user.email || '');
        
        if (pushSuccess) {
          setMagicBellStatus('success');
          console.log('✅ Push subscription successful');
          
          // Track success
          trackEvent('push_subscription_success', {
            user_id: user.uid,
            push_supported: isPushNotificationSupported(),
          });

          // Show test notification
          setTimeout(() => {
            new Notification('Notifications Enabled! 🎉', {
              body: 'You will now receive game alerts, poll notifications, and announcements.',
              icon: '/vite.svg',
              badge: '/vite.svg',
            });
          }, 500);

          // Close prompt after brief success message
          setTimeout(() => {
            setVisible(false);
          }, 2000);
        } else {
          setMagicBellStatus('error');
          console.warn('⚠️ Push subscription failed, but browser notifications enabled');
          
          // Track partial success
          trackEvent('push_subscription_failed', {
            user_id: user.uid,
          });

          // Still show test notification for browser notifications
          new Notification('Browser Notifications Enabled', {
            body: 'Push notifications will be set up shortly.',
            icon: '/vite.svg',
          });

          // Close after error shown
          setTimeout(() => {
            setVisible(false);
          }, 3000);
        }
      } else if (result === 'denied') {
        console.log('❌ Notification permission denied');
        
        // Track denial
        trackEvent('notification_permission_denied', {
          user_id: user.uid,
        });
        
        // Don't show again if denied
        localStorage.setItem(PERMISSION_DISMISSED_KEY, 'true');
        setVisible(false);
      } else {
        // User dismissed the browser prompt
        setVisible(false);
      }
    } catch (error) {
      console.error('❌ Error requesting notification permission:', error);
      setMagicBellStatus('error');
      
      // Track error
      trackEvent('notification_permission_error', {
        user_id: user?.uid,
        error: error instanceof Error ? error.message : 'Unknown error',
      });
      
      setTimeout(() => {
        setVisible(false);
      }, 2000);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDismiss = () => {
    // Increment dismiss count
    const dismissCount = parseInt(localStorage.getItem(PERMISSION_DISMISS_COUNT_KEY) || '0', 10);
    const newCount = dismissCount + 1;
    localStorage.setItem(PERMISSION_DISMISS_COUNT_KEY, newCount.toString());

    // Set snooze period
    const snoozeDate = new Date();
    snoozeDate.setDate(snoozeDate.getDate() + SNOOZE_DAYS);
    localStorage.setItem(PERMISSION_SNOOZE_KEY, snoozeDate.toISOString());

    // Track analytics
    trackEvent('notification_prompt_dismissed', {
      user_id: user?.uid,
      dismiss_count: newCount,
      snooze_until: snoozeDate.toISOString(),
    });

    setVisible(false);
    console.log(`📢 Notification prompt snoozed for ${SNOOZE_DAYS} days (dismissal ${newCount}/${MAX_DISMISSALS})`);
  };

  const handleNeverAsk = () => {
    localStorage.setItem(PERMISSION_DISMISSED_KEY, 'true');
    
    // Track analytics
    trackEvent('notification_prompt_never_ask', {
      user_id: user?.uid,
    });
    
    setVisible(false);
    setShowMobileButton(false);
    console.log('📢 Notification prompt permanently dismissed');
  };

  const handleMobileButtonDismiss = () => {
    setShowMobileButton(false);
    handleDismiss();
  };

  // Mobile sticky button UI
  if (showMobileButton && permission === 'default') {
    console.log('📢 Rendering mobile notification banner');
    return (
      <div 
        className="fixed top-0 left-0 right-0 z-[9999] bg-gradient-to-r from-blue-500 to-blue-600 text-white shadow-lg"
        style={{ 
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          zIndex: 9999,
        }}
      >
        <div className="max-w-7xl mx-auto px-3 py-2 sm:px-4 sm:py-3 flex items-center justify-between gap-2 sm:gap-3">
          <div className="flex items-center gap-2 sm:gap-3 flex-1 min-w-0">
            <Bell className="w-5 h-5 flex-shrink-0" />
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium">Enable notifications</p>
              <p className="text-xs text-blue-100 hidden sm:block truncate">Get alerts for games & polls</p>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            <Button
              onClick={handleRequestPermission}
              size="sm"
              className="bg-white text-blue-600 hover:bg-blue-50 font-semibold"
              disabled={isLoading}
            >
              {isLoading ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                'Allow'
              )}
            </Button>
            <button
              onClick={handleMobileButtonDismiss}
              className="p-1 rounded-full hover:bg-blue-700 transition-colors"
              aria-label="Dismiss"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!visible || permission !== 'default') {
    return null;
  }

  return (
    <div className="fixed bottom-4 right-4 z-50 animate-slide-up max-w-sm">
      <Card className="shadow-2xl border-2 border-blue-500">
        <CardContent className="p-6 relative">
          {!isLoading && (
            <button
              onClick={handleDismiss}
              className="absolute top-2 right-2 p-1 rounded-full hover:bg-gray-100 transition-colors"
              aria-label="Dismiss"
            >
              <X className="w-4 h-4 text-gray-500" />
            </button>
          )}

          <div className="flex items-start gap-4">
            <div className="flex-shrink-0 w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
              {magicBellStatus === 'success' ? (
                <CheckCircle className="w-6 h-6 text-green-600" />
              ) : magicBellStatus === 'connecting' ? (
                <Loader2 className="w-6 h-6 text-blue-600 animate-spin" />
              ) : (
                <Bell className="w-6 h-6 text-blue-600" />
              )}
            </div>

            <div className="flex-1">
              {magicBellStatus === 'success' ? (
                <>
                  <h3 className="text-lg font-semibold text-green-900 mb-2">
                    All Set! 🎉
                  </h3>
                  <p className="text-sm text-green-700">
                    You'll now receive notifications for new games, polls, and announcements.
                  </p>
                </>
              ) : magicBellStatus === 'connecting' ? (
                <>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">
                    Setting Up... ⚙️
                  </h3>
                  <p className="text-sm text-gray-600">
                    Connecting to notification service...
                  </p>
                </>
              ) : magicBellStatus === 'error' ? (
                <>
                  <h3 className="text-lg font-semibold text-orange-900 mb-2">
                    Almost There! ⚠️
                  </h3>
                  <p className="text-sm text-orange-700">
                    Browser notifications enabled. Push notifications will be configured shortly.
                  </p>
                </>
              ) : (
                <>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">
                    Stay in the Loop! 🎮
                  </h3>
                  <p className="text-sm text-gray-600 mb-3">
                    Get notified when:
                  </p>
                  <ul className="text-xs text-gray-600 mb-4 space-y-1">
                    <li className="flex items-center gap-2">
                      <span className="text-blue-500">•</span>
                      <span>New trivia games start</span>
                    </li>
                    <li className="flex items-center gap-2">
                      <span className="text-blue-500">•</span>
                      <span>Polls and votes are posted</span>
                    </li>
                    <li className="flex items-center gap-2">
                      <span className="text-blue-500">•</span>
                      <span>Important announcements are made</span>
                    </li>
                  </ul>

                  <div className="flex flex-col gap-2">
                    <Button
                      onClick={handleRequestPermission}
                      className="w-full"
                      disabled={isLoading}
                    >
                      {isLoading ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Enabling...
                        </>
                      ) : (
                        <>
                          <Bell className="w-4 h-4 mr-2" />
                          Enable Notifications
                        </>
                      )}
                    </Button>
                    
                    <div className="flex gap-2">
                      <Button
                        onClick={handleDismiss}
                        variant="outline"
                        size="sm"
                        className="flex-1"
                        disabled={isLoading}
                      >
                        Remind Me Later
                      </Button>
                      <Button
                        onClick={handleNeverAsk}
                        variant="ghost"
                        size="sm"
                        className="flex-1 text-gray-500"
                        disabled={isLoading}
                      >
                        Don't Ask Again
                      </Button>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>

          {magicBellStatus === 'idle' && (
            <div className="mt-4 pt-4 border-t border-gray-200">
              <p className="text-xs text-gray-500">
                💡 Tip: Install this app on your device for the best experience!
              </p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

export default NotificationPermissionPrompt;

